import html2canvas from "html2canvas";
import DOMPurify from "dompurify";
import { TFunction } from "i18next";
import { PDFDocument } from "pdf-lib";

type HtmlToPdfInput = {
  titleText?: string;
  bodyHtml: string;
};

type HtmlToPdfDeps = {
  html2canvas?: typeof html2canvas;
  dompurify?: typeof DOMPurify;
};

/**
 * Wait one browser frame so the DOM has a chance to lay out before we screenshot it.
 */
function waitNextFrame() {
  return new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Wait for all <img> elements inside `root` to finish loading.
 * Without this, html2canvas can capture blank/missing images.
 */
async function waitForImages(root: HTMLElement) {
  const imgs = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve(); // don't block forever if an image fails to load
          })
    )
  );
}

/**
 * Convert a canvas to JPEG bytes (used to embed the screenshot in the PDF).
 */
async function canvasToJpegBytes(canvas: HTMLCanvasElement, t: TFunction, quality = 0.85): Promise<Uint8Array> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(t("pdfConversion.errors.failedToEncodeCanvas")))),
      "image/jpeg",
      quality
    );
  });
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Convert an HTML string to a PDF by:
 * 1) sanitising the HTML (security)
 * 2) rendering it into the DOM (hidden)
 * 3) taking a screenshot with html2canvas
 * 4) splitting the screenshot into A4 pages and embedding into a PDF
 *
 * Note: this is a raster (screenshot) PDF, not a selectable-text PDF.
 */
export async function htmlStringToPdfBlobRaster(
  input: HtmlToPdfInput,
  t: TFunction,
  deps: HtmlToPdfDeps = {}
): Promise<Blob> {
  // Dependency injection so we can mock html2canvas/DOMPurify in tests.
  const renderToCanvas = deps.html2canvas ?? html2canvas;
  const purifier = deps.dompurify ?? DOMPurify;

  // Host container that we add to the DOM temporarily.
  // It's fixed so layout is stable and not affected by surrounding UI.
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.inset = "0";
  host.style.background = "#ffffff";
  host.style.overflow = "auto";
  host.style.zIndex = "0";

  // Content wrapper sized to ~A4 width in pixels (at 96 DPI-ish).
  const content = document.createElement("div");
  content.style.width = "794px";
  content.style.margin = "0 auto";
  content.style.padding = "24px";
  content.style.background = "#ffffff";

  const wrapper = document.createElement("div");
  wrapper.style.fontFamily = "Arial, sans-serif";
  wrapper.style.fontSize = "12px";
  wrapper.style.lineHeight = "1.4";
  wrapper.style.color = "#111";

  // Optional title at the top of the page.
  if (input.titleText) {
    const h2 = document.createElement("h2");
    h2.style.margin = "0 0 12px";
    h2.textContent = input.titleText;
    wrapper.appendChild(h2);
  }

  // Sanitize the HTML before inserting it into the DOM.
  // This reduces the risk of XSS or unexpected content.
  const safeFragment = purifier.sanitize(input.bodyHtml, {
    RETURN_DOM_FRAGMENT: true,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    ALLOWED_ATTR: ["src", "href", "alt", "title", "colspan", "rowspan"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|data):|#)/i,
  });

  wrapper.appendChild(safeFragment);
  content.appendChild(wrapper);
  host.appendChild(content);
  document.body.appendChild(host);

  try {
    // Give the browser a moment to lay out and load fonts/images.
    await waitNextFrame();
    const fontsAny = document as any;
    if (fontsAny.fonts?.ready) await fontsAny.fonts.ready;

    await waitForImages(content);

    // Render the content area to a canvas (a screenshot).
    const canvas = await renderToCanvas(content, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });

    // Build a PDF by slicing the tall canvas into A4-height chunks.
    const pdfDoc = await PDFDocument.create();
    const pageW = 595.28; // A4 width in points
    const pageH = 841.89; // A4 height in points

    // Convert between canvas pixels and PDF "points".
    const pxPerPt = canvas.width / pageW;
    const pageHeightPx = Math.floor(pageH * pxPerPt);

    let y = 0;
    while (y < canvas.height) {
      const sliceH = Math.min(pageHeightPx, canvas.height - y);

      // Create a slice canvas for one PDF page.
      const slice = document.createElement("canvas");
      slice.width = canvas.width;
      slice.height = sliceH;

      const ctx = slice.getContext("2d");
      if (!ctx) throw new Error(t("pdfConversion.errors.canvasContextNotAvailable"));

      // White background so we don't get transparency artefacts.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, slice.width, slice.height);

      // Copy part of the full canvas into this slice.
      ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      // Embed the slice as a JPEG image into the PDF.
      const jpgBytes = await canvasToJpegBytes(slice, t, 0.85);
      const jpg = await pdfDoc.embedJpg(jpgBytes);

      const page = pdfDoc.addPage([pageW, pageH]);
      const sliceHPt = sliceH / pxPerPt;

      // Draw the image at the top of the page.
      page.drawImage(jpg, { x: 0, y: pageH - sliceHPt, width: pageW, height: sliceHPt });

      y += sliceH;
    }

    const pdfBytes = await pdfDoc.save();
    return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
  } finally {
    // Always clean up the temporary DOM nodes.
    host.remove();
  }
}
